import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const STEPS = [
  {
    id: 1,
    label: '철거',
    week: '1주차',
    icon: 'ri-hammer-line',
    desc: '기존 바닥재·벽체·천장 철거 및 폐기물 처리',
    image: 'https://public.readdy.ai/ai/img_res/edited_5191e4d1a59ade307b43d98162fc7a2b_2b18175b.jpg',
  },
  {
    id: 2,
    label: '설비/배관',
    week: '1~2주차',
    icon: 'ri-tools-line',
    desc: '급수·배수·난방 배관 교체 및 방수 처리',
    image: 'https://public.readdy.ai/ai/img_res/de031b90996124248887db22d37aea9a.jpg',
  },
  {
    id: 3,
    label: '목공',
    week: '2~3주차',
    icon: 'ri-layout-grid-line',
    desc: '벽체 구성·천장 틀·붙박이장 제작 설치',
    image: 'https://readdy.ai/api/search-image?query=carpentry%20framing%20gypsum%20board%20ceiling%20installation%20apartment%20renovation%20construction%20site%20professional%20worker%20realistic%20photography%20wide%20angle%20dramatic%20interior&width=800&height=500&seq=home-info-03&orientation=landscape',
  },
  {
    id: 4,
    label: '전기/조명',
    week: '3주차',
    icon: 'ri-flashlight-line',
    desc: '분전반 교체·배선·조명 기구 설치',
    image: 'https://readdy.ai/api/search-image?query=electrical%20wiring%20installation%20apartment%20renovation%20construction%20site%20electrician%20worker%20conduit%20pipes%20ceiling%20realistic%20photography%20professional%20wide%20angle%20dramatic&width=800&height=500&seq=home-info-04&orientation=landscape',
  },
  {
    id: 5,
    label: '타일/바닥',
    week: '3~4주차',
    icon: 'ri-grid-line',
    desc: '욕실·주방 타일 및 거실·방 마루 시공',
    image: 'https://readdy.ai/api/search-image?query=bathroom%20tile%20installation%20worker%20laying%20large%20format%20porcelain%20tiles%20apartment%20renovation%20realistic%20photography%20professional%20construction%20wide%20angle%20dramatic&width=800&height=500&seq=home-info-05&orientation=landscape',
  },
  {
    id: 6,
    label: '도장/도배',
    week: '4주차',
    icon: 'ri-paint-brush-line',
    desc: '친환경 페인트 도장 및 실크 벽지 시공',
    image: 'https://readdy.ai/api/search-image?query=interior%20painting%20worker%20rolling%20white%20paint%20on%20wall%20apartment%20renovation%20professional%20painter%20realistic%20photography%20construction%20site%20wide%20angle%20dramatic&width=800&height=500&seq=home-info-06&orientation=landscape',
  },
  {
    id: 7,
    label: '가구/마감',
    week: '4~5주차',
    icon: 'ri-home-smile-line',
    desc: '주방 가구·욕실 기기 설치 및 최종 점검',
    image: 'https://readdy.ai/api/search-image?query=completed%20modern%20apartment%20interior%20renovation%20finished%20living%20room%20clean%20minimalist%20design%20professional%20photography%20elegant%20warm%20lighting%20beautiful%20wide%20angle&width=800&height=500&seq=home-info-07&orientation=landscape',
  },
];

export default function InfoPreview() {
  const [hovered, setHovered] = useState<number | null>(null);
  const activeStep = hovered !== null ? STEPS[hovered] : STEPS[0];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ threshold: 0.08 });
  const { ref: statsRef, getItemStyle: getStatStyle } = useStaggerAnimation(4, { threshold: 0.1 });

  return (
    <section className="relative py-28 md:py-36 overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('https://storage.readdy-site.link/project_files/0477f867-7633-4e4c-8292-18c2b50a08d2/c417aa41-fc84-46e6-9709-4f9e15c12d1b_unnamed.jpg?v=8130b9857c58ef05388c818e0de13a20')",
        }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/70" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">

        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-16 gap-4 md:gap-6 text-center md:text-left"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(36px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div>
            <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">
              Interior Guide
            </span>
            <h2
              className="text-3xl md:text-5xl font-light text-white mt-3 md:mt-4 leading-tight"
              style={{ fontFamily: "'Noto Serif KR', serif" }}
            >
              인테리어 공정 가이드
            </h2>
            <p className="text-white/60 text-xs md:text-sm mt-2 md:mt-3">
              30평 기준 4~5주 · 7단계 체계적인 시공 프로세스
            </p>
          </div>
          <Link
            to="/info"
            className="inline-flex items-center justify-center md:justify-start gap-2 text-white/80 text-sm font-medium border-b border-white/40 pb-1 whitespace-nowrap cursor-pointer hover:text-[#c9a84c] hover:border-[#c9a84c] transition-colors mx-auto md:mx-0"
          >
            공정표 전체 보기
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Main Content */}
        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col lg:flex-row gap-8 items-stretch"
          style={{
            opacity: contentVisible ? 1 : 0,
            transform: contentVisible ? 'translateY(0)' : 'translateY(48px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          {/* Step List */}
          <div className="lg:w-80 shrink-0 flex flex-col gap-2">
            {STEPS.map((step, idx) => {
              const isActive = hovered === idx || (hovered === null && idx === 0);
              return (
                <Link
                  key={step.id}
                  to="/info"
                  onMouseEnter={() => setHovered(idx)}
                  onMouseLeave={() => setHovered(null)}
                  className="group flex items-center gap-4 px-5 py-4 rounded-xl transition-all duration-300 cursor-pointer"
                  style={{
                    backgroundColor: isActive ? '#c9a84c22' : 'rgba(255,255,255,0.06)',
                    border: isActive ? '1px solid #c9a84c66' : '1px solid rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all duration-300"
                    style={{
                      backgroundColor: isActive ? '#c9a84c' : 'rgba(255,255,255,0.15)',
                      color: isActive ? '#0d0d0d' : 'rgba(255,255,255,0.7)',
                    }}
                  >
                    {step.id}
                  </span>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 transition-all duration-300"
                      style={{ backgroundColor: isActive ? '#c9a84c22' : 'rgba(255,255,255,0.08)' }}
                    >
                      <i
                        className={`${step.icon} text-sm transition-colors duration-300`}
                        style={{ color: isActive ? '#c9a84c' : 'rgba(255,255,255,0.55)' }}
                      />
                    </div>
                    <div className="min-w-0">
                      <p
                        className="text-sm font-semibold transition-colors duration-300 whitespace-nowrap"
                        style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.65)' }}
                      >
                        {step.label}
                      </p>
                      <p className="text-xs text-white/40 whitespace-nowrap">{step.week}</p>
                    </div>
                  </div>
                  <i
                    className="ri-arrow-right-s-line text-base transition-all duration-300 shrink-0"
                    style={{
                      color: isActive ? '#c9a84c' : 'transparent',
                      transform: isActive ? 'translateX(0)' : 'translateX(-4px)',
                    }}
                  />
                </Link>
              );
            })}
          </div>

          {/* Right — Image + Description */}
          <div className="flex-1 flex flex-col gap-4">
            <div className="relative w-full flex-1 min-h-[320px] lg:min-h-0 rounded-2xl overflow-hidden">
              <img
                key={activeStep.id}
                src={activeStep.image}
                alt={activeStep.label}
                className="w-full h-full object-cover object-top transition-opacity duration-500"
                style={{ minHeight: '320px' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute top-5 left-5 flex items-center gap-2">
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-[#0d0d0d]"
                  style={{ backgroundColor: '#c9a84c' }}
                >
                  {activeStep.id}
                </span>
                <span className="text-white text-sm font-semibold bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm">
                  {activeStep.label} 공정
                </span>
              </div>
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                <div>
                  <p className="text-white font-semibold text-base mb-1">{activeStep.label} 공정</p>
                  <p className="text-white/70 text-sm">{activeStep.desc}</p>
                </div>
                <span
                  className="text-xs font-semibold px-3 py-1.5 rounded-full shrink-0 whitespace-nowrap"
                  style={{ backgroundColor: '#c9a84c22', color: '#c9a84c', border: '1px solid #c9a84c44' }}
                >
                  {activeStep.week}
                </span>
              </div>
            </div>
            <div
              className="rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(201,168,76,0.25)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: '#c9a84c22' }}
                >
                  <i className="ri-file-list-3-line text-base" style={{ color: '#c9a84c' }} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">공정별 자재 및 상품 정보도 확인하세요</p>
                  <p className="text-white/50 text-xs mt-0.5">각 공정에 필요한 추천 자재와 예상 단가를 제공합니다</p>
                </div>
              </div>
              <Link
                to="/info"
                className="bg-[#c9a84c] text-[#0d0d0d] text-sm font-bold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#b8973b] transition-colors"
              >
                자세히 보기
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom stats row */}
        <div
          ref={statsRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10"
        >
          {[
            { value: '7단계', label: '체계적인 공정', icon: 'ri-list-check-2' },
            { value: '4~5주', label: '30평 기준 기간', icon: 'ri-calendar-line' },
            { value: '28종+', label: '공정별 추천 자재', icon: 'ri-box-3-line' },
            { value: '전문가 TIP', label: '각 공정 노하우 제공', icon: 'ri-lightbulb-flash-line' },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className="flex items-center gap-3 px-5 py-4 rounded-xl"
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(8px)',
                ...getStatStyle(i),
              }}
            >
              <div
                className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0"
                style={{ backgroundColor: '#c9a84c14' }}
              >
                <i className={`${stat.icon} text-sm`} style={{ color: '#c9a84c' }} />
              </div>
              <div>
                <p className="text-white font-bold text-sm">{stat.value}</p>
                <p className="text-white/55 text-xs">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
